import { Box, Paper, TableBody, TableCell, TableRow } from '@mui/material'
import { HeadCell } from '@common/components/Table/table.types'
import { MouseEvent } from 'react'
import EnhancedTable from '@common/components/Table/EnhancedTable'
import { useGetSubjectsList } from '../../hooks/useGetSubjectsList'
import { TimepointSubject } from '@common/config/api/client'
import SubjectsTimepointsTableBody from './SubjectsTimepointsTableBody'
import SubjectsTimepointsTableToolbar from './SubjectsTimepointsTableToolbar'
import { SubjectsTimepointsParams } from '../../types'
import { SearchOff } from '@mui/icons-material'
import EmptyState from '@common/components/EmptyState/EmptyState'

export interface SubjectsTimepointsTableProps {
  searchValue?: string
  page: number
  onSearch: (value: string) => void
  onAddClick: () => void
  subjectsTimepointsParams: SubjectsTimepointsParams
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onRequestSort: (event: MouseEvent<unknown>, property: keyof TimepointSubject) => void
}

export default function SubjectsTimepointsTable({
  searchValue,
  page,
  onSearch,
  onAddClick,
  subjectsTimepointsParams,
  onPageChange,
  onRowsPerPageChange,
  onRequestSort,
}: Readonly<SubjectsTimepointsTableProps>) {
  const { data, error, isLoading } = useGetSubjectsList(['SubjectsList'], subjectsTimepointsParams)

  if (isLoading) {
    return <div>Loading...</div>
  }
  if (error) {
    return <div>Error: {error.message}</div>
  }

  const headCells: readonly HeadCell<TimepointSubject>[] = [
    { disablePadding: false, id: 'pseudo_id', label: 'Subject', disableSort: false, stickyColumn: true, width: 200 },
    {
      disablePadding: false,
      id: 'blind_code',
      label: 'Blind Code',
      disableSort: false,
      stickyColumn: true,
      width: 120,
    },
    {
      disablePadding: false,
      id: 'site_id' as keyof TimepointSubject,
      label: 'Site ID',
      disableSort: true,
      stickyColumn: true,
      width: 120,
    },
    { disablePadding: false, id: 'status', label: 'Status', disableSort: false, stickyColumn: true, width: 120 },
    {
      disablePadding: false,
      id: 'query_status' as keyof TimepointSubject,
      label: 'Query',
      disableSort: true,
      stickyColumn: true,
      width: 120,
    },
    ...(data?.data?.study_timepoints || []).map((tp) => ({
      disablePadding: false,
      id: tp.timepoint_id as keyof TimepointSubject,
      label: tp.name,
      disableSort: true,
      minWidth: 220,
    })),
  ]

  const subjects = data?.data?.subjects || []
  const hasSubjects = subjects.length > 0

  return (
    <Box component="section">
      <SubjectsTimepointsTableToolbar onSearch={onSearch} searchValue={searchValue} onAddClick={onAddClick} />
      <Paper
        sx={{
          padding: 3,
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
        }}
      >
        <EnhancedTable<TimepointSubject>
          rows={[]}
          page={page}
          headCells={headCells}
          orderBy={subjectsTimepointsParams.order_by_field as keyof TimepointSubject}
          order={subjectsTimepointsParams.asc_order ? 'asc' : 'desc'}
          expandable
          rowsPerPage={subjectsTimepointsParams.limit}
          onRequestSort={onRequestSort}
          onChangePage={onPageChange}
          onChangeRowsPerPage={onRowsPerPageChange}
          next_cursor={data?.data?.pagination?.next_cursor}
          previous_cursor={data?.data?.pagination?.previous_cursor}
        >
          {hasSubjects ? (
            <SubjectsTimepointsTableBody
              rows={subjects}
              order={subjectsTimepointsParams.asc_order ? 'asc' : 'desc'}
              orderBy={subjectsTimepointsParams.order_by_field as keyof TimepointSubject}
              limit={subjectsTimepointsParams.limit}
              previous_cursor={data?.data?.pagination?.previous_cursor}
              next_cursor={data?.data?.pagination?.next_cursor}
            />
          ) : (
            <TableBody>
              <TableRow>
                <TableCell colSpan={100} align="center" sx={{ border: 'none' }}>
                  <EmptyState
                    icon={<SearchOff sx={{ fontSize: 40, color: 'text.secondary' }} />}
                    title="No matching subjects found"
                    message={
                      searchValue
                        ? `No subjects match "${searchValue}". Try a different search term.`
                        : 'No subjects available for this study'
                    }
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </EnhancedTable>
      </Paper>
    </Box>
  )
}
